import React, { createContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useGetMatchRosterRoundQuery } from 'common/store/modules/api/cfs-api';
import { useGetMatchQuery } from 'common/store/modules/api/afl-api';
import { getTranslation } from 'common/react/utils/translations';
import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import { LastUpdated } from 'common/react/components/LastUpdated';
import {
    FieldListView,
    InsAndOuts,
    LateChanges,
    Milestones
} from 'widgets/team-lineups-v2/js/components/item/components';
import { TeamLineupsLegend } from 'widgets/team-lineups-v2/js/components/item/TeamLineupsLegend';
import { MCTeamLineupsSkeletonLoader } from './MCTeamLineupsSkeleton';
import { EmptyState } from 'common/react/components/EmptyState';

/**
 * Fetches the match and roster data.
 *
 * @param {string} matchId The Pulse match ID.
 * @param {string} providerCompetitionId The provider competition ID.
 * @param {string} providerRoundId The provider round ID.
 *
 * @returns {JSX.Element} The empty state or the Match Centre Team Lineups.
 */

export const TeamLineupsContext = createContext('');

export const MCTeamLineups = ({
    matchId,
    providerCompetitionId,
    providerRoundId,
    providerMatchId
}) => {
    const [selectedTeam, setSelectedTeam] = useState('-1');
    const [selectedTabView, setSelectedTabView] = useState('field');

    // Capture team and view (field/list) selection in state
    const handleTeamChange = (team) => {
        setSelectedTeam(team);
    };

    const handleTabViewChange = (view) => {
        setSelectedTabView(view);
    };

    const {
        data: rosterData,
        isFetching: isFetchingRosterData,
        isError: rosterDataError
    } = useGetMatchRosterRoundQuery({ roundId: providerRoundId });

    const {
        data: singleMatchData,
        isFetching: isFetchingSingleMatchData,
        isError: singleMatchDataError
    } = useGetMatchQuery({ id: matchId });

    const matchRosterData = rosterData?.find(
        (roster) => roster.match.matchId === providerMatchId
    );

    const shouldShowSubs = PULSE.app.common.match.shouldShowSubs(
        matchRosterData?.matchRoster?.status
    );

    // Determine what team value has been selected
    const objHasValue = (obj, value) => Object.values(obj).includes(value);
    let teamDisplay = '';
    if (singleMatchData) {
        if (selectedTeam !== '-1') {
            if (objHasValue(singleMatchData?.[0]?.home?.team, selectedTeam)) {
                teamDisplay = PULSE.app.common.CONSTANTS.TEAM.HOME;
            } else {
                teamDisplay = PULSE.app.common.CONSTANTS.TEAM.AWAY;
            }
        } else {
            teamDisplay = PULSE.app.common.CONSTANTS.TEAM.BOTH;
        }
    }

    const isAFLW =
        PULSE.app.common.match
            .getCompAbbr(providerCompetitionId)
            .toLowerCase() === 'aflw';

    const selectedRegion = useSingleSiteSetting('region');
    const lastUpdated = moment(matchRosterData?.matchRoster?.lastUpdated);

    return (
        <>
            {isFetchingRosterData || isFetchingSingleMatchData ? (
                <MCTeamLineupsSkeletonLoader />
            ) : rosterDataError ||
              singleMatchDataError ||
              !rosterData ||
              !singleMatchData ? (
                <EmptyState
                    title={getTranslation('label.teamLineups.noDataTitle')}
                    summary={getTranslation('label.teamLineups.noDataText')}
                    cssClass="competition-nav__empty-state"
                />
            ) : matchRosterData && singleMatchData ? (
                <>
                    <TeamLineupsContext.Provider value={'match-centre'}>
                        <div className="mc-team-lineups__inner-wrapper">
                            <FieldListView
                                match={matchRosterData}
                                isAFLW={isAFLW}
                                onTeamChange={handleTeamChange}
                                onTabViewChange={handleTabViewChange}
                            />
                            <InsAndOuts
                                match={matchRosterData}
                                shouldShowSubs={shouldShowSubs}
                                teamDisplay={teamDisplay}
                                isAFLW={isAFLW}
                                selectedTabView={selectedTabView}
                            />
                            <div className="mc-team-lineups__late-changes-wrapper team-lineups__sub-section">
                                <Milestones
                                    match={matchRosterData}
                                    teamDisplay={teamDisplay}
                                    selectedTabView={selectedTabView}
                                />
                                {shouldShowSubs && (
                                    <LateChanges
                                        match={matchRosterData}
                                        teamDisplay={teamDisplay}
                                        selectedTabView={selectedTabView}
                                    />
                                )}
                            </div>
                            <LastUpdated
                                cssClass="team-lineups__last-updated"
                                lastUpdated={lastUpdated}
                                selectedRegion={selectedRegion}
                            />
                        </div>
                        <TeamLineupsLegend />
                    </TeamLineupsContext.Provider>
                </>
            ) : (
                <EmptyState
                    title={getTranslation('label.teamLineups.noDataTitle')}
                    summary={getTranslation('label.teamLineups.noDataText')}
                    cssClass="competition-nav__empty-state"
                />
            )}
        </>
    );
};

MCTeamLineups.propTypes = {
    matchId: PropTypes.string.isRequired,
    providerCompetitionId: PropTypes.string.isRequired,
    providerRoundId: PropTypes.string.isRequired,
    providerMatchId: PropTypes.string.isRequired
};
